/**
 * Render shell. (§6)
 *
 * This file used to BE the placeholder renderer (capsules + a canvas pitch).
 * For M7 it became a thin shell: it owns the WebGLRenderer, the Scene and the
 * camera, captures each frame the netcode pushes through the setters into a
 * `RenderState`, and delegates all drawing to `RenderSubsystem`s (pitch,
 * players, lighting, stadium, VFX, post) built by `subsystems.ts`.
 *
 * ⚠ The public API below is FROZEN — `main.ts` and nothing else drives it, and
 * that seam is exactly what keeps M7 from regressing M1–M6 gameplay. Do not
 * change a signature here without changing main.ts in the same commit. The
 * methods, verbatim, that main.ts calls:
 *   new Renderer(container) · resize() · setPitch(config) · beginFrame() ·
 *   setPlayer(id,x,y,z,yaw,team) · setBall(x,y,z,qx,qy,qz,qw) ·
 *   setBallHold(slot,pose) ·
 *   setControlled(x,y,z,blend,pulseS,yaw) · hideControlled() ·
 *   setBoostAura(slot,x,y,z,tier) ·
 *   setBallArrow(show,x,y,z,yaw) · setPassTarget(x,y,z) ·
 *   setAimArrow(show,x,y,z,yaw,charge,curl) · signalReferee(kind,tick,x,z,phase) ·
 *   applyCamera(pose) · render() · dispose()
 *
 * Subsystems are wired in ONE place (subsystems.ts) so the five that implement
 * them never collide on this file. If a subsystem file is absent, comment it out
 * of the registry — the shell runs with whatever it is given.
 */

import {
  Color,
  PerspectiveCamera,
  Scene,
  Vector3,
  WebGLRenderer,
} from 'three';
import { MAX_PLAYERS, MatchPhase, animExecToken, type MatchConfig } from '@fg/shared/sim';
import type { CameraPose } from './camera.js';
import {
  type CompositeSubsystem,
  type QualityTier,
  type RenderContext,
  type RenderState,
  type RenderSubsystem,
  QUALITY_PRESETS,
  IMPOSTOR_LAYER,
  autoDetectQuality,
  isCompositeSubsystem,
} from './context.js';
import {
  RefSignalKind,
  ageRefSignal,
  createRefSignal,
  raiseRefSignal,
} from './refSignal.js';
import { BallHoldPose, createAttachmentRegistry } from './handAttach.js';
import type { CelebrationHandle } from './celebration.js';
import type { CastVisualHandle } from './player/castPlayerVisual.js';
import type { CrowdControl } from './stadium.js';
import { buildSubsystems } from './subsystems.js';
import { setKTX2Renderer } from './player/castDecoders.js';
import type { PlayerVisualHandle } from './player/playerVisual.js';

/** Ball rest height = its radius; used to seed the render state before kickoff. */
const BALL_START_Y = 0.11;

/** Placeholder buffer for a frame with no corner trail; never drawn (count 0). */
const EMPTY_TRAIL = new Float32Array(0);

/**
 * A slot whose position has not changed for this long is STANDING, not merely
 * between sim ticks, and its derived velocity drops to zero.
 *
 * The sim steps at 60 Hz, so a moving player produces a new sample every
 * 16.7 ms. Three ticks of silence is unambiguous, and short enough that the
 * stop still lands inside the animator's own 0.12-0.22 s crossfade — the pose
 * settles into Idle on the same blend it would have anyway. Much shorter and a
 * dropped frame or a hitching tab reads as a stop; much longer and a player who
 * halts keeps striding on the spot for a visible moment.
 */
const STILL_AFTER_S = 0.05;

function createRenderState(): RenderState {
  return {
    active: new Uint8Array(MAX_PLAYERS),
    team: new Uint8Array(MAX_PLAYERS),
    px: new Float32Array(MAX_PLAYERS),
    py: new Float32Array(MAX_PLAYERS),
    pz: new Float32Array(MAX_PLAYERS),
    yaw: new Float32Array(MAX_PLAYERS),
    prevPx: new Float32Array(MAX_PLAYERS),
    prevPy: new Float32Array(MAX_PLAYERS),
    prevPz: new Float32Array(MAX_PLAYERS),
    velX: new Float32Array(MAX_PLAYERS),
    velZ: new Float32Array(MAX_PLAYERS),
    sampleAgeS: new Float32Array(MAX_PLAYERS),
    anim: new Uint8Array(MAX_PLAYERS),
    animExec: new Uint8Array(MAX_PLAYERS),
    dt: 1 / 60,
    elapsed: 0,
    ball: {
      px: 0, py: BALL_START_Y, pz: 0, qx: 0, qy: 0, qz: 0, qw: 1, speed: 0,
      powerKick: false, possessedBy: -1,
    },
    ballPrev: { px: 0, py: BALL_START_Y, pz: 0 },
    ballHeldBy: -1,
    ballHoldPose: BallHoldPose.None,
    controlledShow: false,
    controlledX: 0,
    controlledY: 0,
    controlledZ: 0,
    controlledBlend: 0,
    controlledPulse: 0,
    controlledYaw: 0,
    boostAuraTier: 0,
    boostAuraSlot: -1,
    boostAuraX: 0,
    boostAuraY: 0,
    boostAuraZ: 0,
    ballArrowShow: false,
    ballArrowX: 0,
    ballArrowY: 0,
    ballArrowZ: 0,
    ballArrowYaw: 0,
    passTargetShow: false,
    passTargetId: -1,
    passTargetX: 0,
    passTargetY: 0,
    passTargetZ: 0,
    aimArrowShow: false,
    cornerTrailShow: false,
    cornerTrailPoints: EMPTY_TRAIL,
    cornerTrailCount: 0,
    cornerTrailLandX: 0,
    cornerTrailLandZ: 0,
    cornerTrailLanded: false,
    cornerTrailExact: true,
    aimArrowX: 0,
    aimArrowY: 0,
    aimArrowZ: 0,
    aimArrowYaw: 0,
    aimArrowCharge: 0,
    aimArrowCurl: 0,
    refSignal: createRefSignal(),
    scoreboard: { show: false, homeName: 'HOME', awayName: 'AWAY', scoreA: 0, scoreB: 0, clockS: 0, phase: '' },
    config: null,
  };
}

/** Scratch for `signalReferee` so the push allocates nothing (§15.7). */
const refRaiseScratch = {
  kind: RefSignalKind.None,
  tick: -1,
  x: 0,
  z: 0,
  phase: MatchPhase.Warmup,
};

/**
 * The scene camera, which unlike any other camera in the app must also see the
 * impostor layer (instanced grass, clumps, crowd, flags — see IMPOSTOR_LAYER).
 * Those meshes are moved off the default layer so that material-substituting
 * passes cannot collapse them at the origin, which means every camera that
 * genuinely wants to draw them has to opt in.
 */
function createSceneCamera(): PerspectiveCamera {
  const cam = new PerspectiveCamera(55, 1, 0.1, 600);
  cam.layers.enable(IMPOSTOR_LAYER);
  return cam;
}

export class Renderer {
  readonly scene = new Scene();
  readonly camera = createSceneCamera();
  private readonly gl: WebGLRenderer;
  private readonly container: HTMLElement;

  private readonly ctx: RenderContext;
  private readonly state: RenderState = createRenderState();
  private readonly subsystems: RenderSubsystem[];
  private readonly compositor: CompositeSubsystem | null;
  private readonly playersHandle: PlayerVisualHandle | null;
  /** Goal-celebration VFX (render/celebration.ts), if registered. */
  private readonly celebrationHandle: CelebrationHandle | null;

  private readonly castHandle: CastVisualHandle | null;
  /** The stadium subsystem's crowd-reaction door (render/stadium.ts §6.6), if registered. */
  private readonly stadiumHandle: CrowdControl | null;

  private readonly lookAt = new Vector3();
  /** Wall clock for deriving dt/elapsed. Graphics is downstream of the sim, so a
   *  clock here is fine — it never feeds a WorldState (§15.2). */
  private lastFrameMs = 0;
  /** Whether a slot has been seen active at least once, to seed prev = current. */
  private readonly everActive = new Uint8Array(MAX_PLAYERS);

  constructor(container: HTMLElement, qualityOverride?: QualityTier) {
    this.container = container;
    this.gl = new WebGLRenderer({
      antialias: false, // TAA/FXAA in the post stack (§6.1) handle AA when enabled
      powerPreference: 'high-performance',
      stencil: false,
    });
    this.gl.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(this.gl.domElement);

    // The Basis transcoder picks its OUTPUT GPU format (ASTC / BPTC / DXT /
    // ETC2 / RGBA fallback) by asking a live renderer what it supports, and the
    // optimised cast's textures are all KTX2. Without this the transcoder has
    // no target and every character draws BLACK with no error anywhere — so it
    // is hooked up at the moment a renderer first exists, not at first use.
    setKTX2Renderer(this.gl);

    // A neutral sky until the lighting rig installs an IBL/background (§6.2).
    this.scene.background = new Color(0x8fbcd4);

    const tier = qualityOverride ?? autoDetectQuality(this.gl);
    this.ctx = {
      gl: this.gl,
      scene: this.scene,
      camera: this.camera,
      quality: { ...QUALITY_PRESETS[tier] },
      viewportWidth: 1,
      viewportHeight: 1,
      // Built before buildSubsystems, because a player subsystem publishes into
      // it during the `init` loop below and the ball subsystem reads it on the
      // very first frame after that.
      attachments: createAttachmentRegistry(),
    };
    this.gl.setPixelRatio(Math.min(window.devicePixelRatio, this.ctx.quality.maxPixelRatio));

    // The one place the five subsystems are named. See subsystems.ts.
    this.subsystems = buildSubsystems(this.ctx);
    this.compositor = this.subsystems.find(isCompositeSubsystem) ?? null;
    // The player system's handle, if the flagship visual is the one in use.
    // It is READ-ONLY diagnostics plus the semantic action door: the animation
    // it draws is driven by `RenderState.anim` inside its own update, not from
    // here, so nothing outside can end up as a second animation authority.
    this.playersHandle =
      (this.subsystems.find(
        (s): s is RenderSubsystem & { handle: PlayerVisualHandle } =>
          'handle' in s && s.name === 'procedural-players',
      )?.handle) ?? null;

    this.celebrationHandle =
      (this.subsystems.find(
        (s): s is RenderSubsystem & { handle: CelebrationHandle } =>
          'handle' in s && s.name === 'celebration-vfx',
      )?.handle) ?? null;

    // The cast's own load-completion signal (castPlayerVisual.ts's `ready`),
    // surfaced so callers can hold a transition screen up until the character
    // models are actually there instead of revealing an empty pitch under it.
    this.castHandle =
      (this.subsystems.find(
        (s): s is RenderSubsystem & { handle: CastVisualHandle } =>
          'handle' in s && s.name === 'cast-players',
      )?.handle) ?? null;

    // CrowdControl (render/stadium.ts) is itself a superset of RenderSubsystem —
    // no nested `.handle`, unlike celebration above — so it's found by name and
    // used directly. BOTH stadium implementations answer here: the procedural
    // bowl ('stadium') and the anime bowl ('stadium-v5') each implement
    // CrowdControl — matching only 'stadium' silently severed the goal roar
    // when the V5 swap landed, since the name check ran before the capability
    // check ever got a look.
    this.stadiumHandle =
      (this.subsystems.find(
        (s): s is RenderSubsystem & CrowdControl =>
          (s.name === 'stadium' || s.name === 'stadium-v5') && 'setExcitement' in s,
      )) ?? null;

    for (const s of this.subsystems) s.init?.(this.ctx);

    this.resize();

    // Dev-only scene handle so live-render QA can introspect the scene graph
    // (subsystem meshes, lights, fog, tone mapping) from the console. Stripped
    // from production builds by the DEV guard.
    if (import.meta.env.DEV) {
      (window as unknown as Record<string, unknown>)['__fg'] = this;
    }
  }

  /** Rebuild pitch + goals + stadium + nets for a match config (§9.2). */
  setPitch(config: MatchConfig): void {
    this.state.config = config;
    for (const s of this.subsystems) s.onPitchConfig?.(config, this.ctx);
  }

  /** Runtime quality change (settings menu, §6.8). */
  setQuality(tier: QualityTier): void {
    this.ctx.quality = { ...QUALITY_PRESETS[tier] };
    this.gl.setPixelRatio(Math.min(window.devicePixelRatio, this.ctx.quality.maxPixelRatio));
    for (const s of this.subsystems) s.setQuality?.(this.ctx.quality, this.ctx);
    this.resize();
  }

  qualityTier(): QualityTier {
    return this.ctx.quality.tier;
  }

  /**
   * Start a frame: roll current→prev, advance the clock, mark all slots stale.
   * The per-frame setPlayer/setBall calls then re-populate the live entities.
   */
  beginFrame(): void {
    const now = typeof performance !== 'undefined' ? performance.now() : this.lastFrameMs + 16.67;
    if (this.lastFrameMs === 0) this.lastFrameMs = now - 16.67;
    // Clamp dt so an alt-tab stall doesn't teleport shaders/wind/trails.
    const dt = Math.min(0.1, Math.max(1e-4, (now - this.lastFrameMs) / 1000));
    this.lastFrameMs = now;
    this.state.dt = dt;
    this.state.elapsed += dt;

    const s = this.state;
    for (let i = 0; i < MAX_PLAYERS; i++) {
      if (s.active[i] === 1) {
        s.prevPx[i] = s.px[i]!;
        s.prevPy[i] = s.py[i]!;
        s.prevPz[i] = s.pz[i]!;
        // The clock the derived velocity is measured against. It runs on RENDER
        // frames because that is the only clock this class has, and it is only
        // ever DIVIDED BY at a frame where the position changed — see setPlayer.
        s.sampleAgeS[i] = (s.sampleAgeS[i] as number) + dt;
      }
      s.active[i] = 0;
    }
    s.ballPrev.px = s.ball.px;
    s.ballPrev.py = s.ball.py;
    s.ballPrev.pz = s.ball.pz;
    // Default the transient indicators off; the setters below re-enable them.
    s.controlledShow = false;
    s.boostAuraTier = 0;
    s.boostAuraSlot = -1;
    s.ballArrowShow = false;
    s.passTargetShow = false;
    s.scoreboard.show = false;
    s.passTargetId = -1;
    s.aimArrowShow = false;
    s.cornerTrailShow = false;
    s.cornerTrailCount = 0;
    s.ballHeldBy = -1;
    s.ballHoldPose = BallHoldPose.None;

    // The referee signal is NOT defaulted off here, because unlike everything
    // above it is never re-fed: the sim raises it once, on the tick it happened
    // (§5.6). It runs its own hold and CLEARS ITSELF — see refSignal.ts. Aged
    // after `dt` is computed above so the hold is measured in real seconds.
    ageRefSignal(s.refSignal, dt);
  }

  /**
   * @param anim the sim's `AnimState` for this slot (D9 seam enrichment).
   * @param animExec the sim's `animExecId` (§11) — the identity of the action
   *   currently showing. Defaults to 0 so a caller that has no simulation behind
   *   it (a review harness) keeps working: a constant means "one execution,
   *   forever", which is exactly right when nothing is accepting actions.
   */
  setPlayer(
    id: number, x: number, y: number, z: number, yaw: number, team: number,
    anim = 0, animExec = 0, vx?: number, vz?: number,
  ): void {
    if (id < 0 || id >= MAX_PLAYERS) return;
    const s = this.state;
    // First sighting of a slot: seed prev = current so derived velocity is 0,
    // not a garbage jump from the origin.
    if (this.everActive[id] === 0) {
      s.prevPx[id] = x;
      s.prevPy[id] = y;
      s.prevPz[id] = z;
      s.velX[id] = 0;
      s.velZ[id] = 0;
      s.sampleAgeS[id] = 0;
      this.everActive[id] = 1;
    }
    // ── GROUND VELOCITY (see RenderState.velX for the defect this replaces) ───
    //
    // ⚠ PREFER THE VELOCITY THE SIM ALREADY KNOWS. Differencing a sampled
    // POSITION can only ever estimate it, and estimating a 60 Hz signal from
    // 150 fps observations is what broke: the observation gap alternates two and
    // three render frames for a tick that is always the same length, so even a
    // change-detecting estimator reads +-25 %. The sim integrates velocity
    // directly; every live caller has `players.vx/vz` in hand and passing it
    // costs nothing.
    if (vx !== undefined && vz !== undefined) {
      s.velX[id] = vx;
      s.velZ[id] = vz;
      s.sampleAgeS[id] = 0;
    } else {
      // FALLBACK for a source with no velocity of its own — replay, which
      // samples its ring on the RENDER clock, so its position changes every
      // frame and the interval below is exactly the render frame. Still
      // change-detecting rather than blind, so a source that does repeat samples
      // degrades to "hold the last reading" instead of flickering to zero.
      //
      // `s.px[id]` still holds the PREVIOUS frame's value here: `beginFrame`
      // copies it into `prevPx` and leaves it alone, and the write below is what
      // replaces it.
      //
      // ⚠ COMPARE STORED AGAINST STORED. `px` is a Float32Array and `x` arrives
      // as a float64 off the sim, so `x !== s.px[id]` is true on virtually every
      // frame whatever the sim did — the difference being nothing but the
      // float32 rounding of the SAME number. That reads as "moved by 1e-7 m",
      // which is a speed of ~0 and lands right back in Idle. Both sides are read
      // out of the array so the comparison is exact.
      const prevX = s.px[id] as number;
      const prevZ = s.pz[id] as number;
      s.px[id] = x;
      s.pz[id] = z;
      const moved = (s.px[id] as number) !== prevX || (s.pz[id] as number) !== prevZ;
      const age = s.sampleAgeS[id] as number;
      if (moved && age > 1e-6) {
        s.velX[id] = ((s.px[id] as number) - prevX) / age;
        s.velZ[id] = ((s.pz[id] as number) - prevZ) / age;
        s.sampleAgeS[id] = 0;
      } else if (age >= STILL_AFTER_S) {
        // Longer than several sim ticks without moving: genuinely stationary (or
        // paused). Holding the last velocity past this point would leave a
        // standing player walking on the spot forever.
        s.velX[id] = 0;
        s.velZ[id] = 0;
      }
    }
    s.active[id] = 1;
    s.team[id] = team === 0 ? 0 : 1;
    s.px[id] = x;
    s.py[id] = y;
    s.pz[id] = z;
    s.yaw[id] = yaw;
    s.anim[id] = anim;
    // Reduced to the WIRE token here, at the one seam both sources cross. The
    // locally-predicted player carries a full int off WorldState and a remote
    // player carries an already-wrapped one off the snapshot; leaving them in
    // different spaces would make switching which player you control read as a
    // new execution on the very first frame after the switch.
    s.animExec[id] = animExecToken(animExec);
  }

  setBall(
    x: number, y: number, z: number, qx: number, qy: number, qz: number, qw: number,
    powerKick = false, possessedBy = -1,
  ): void {
    const b = this.state.ball;
    const dt = this.state.dt;
    const dx = x - b.px;
    const dy = y - b.py;
    const dz = z - b.pz;
    b.speed = Math.sqrt(dx * dx + dy * dy + dz * dz) / dt;
    b.px = x; b.py = y; b.pz = z;
    b.qx = qx; b.qy = qy; b.qz = qz; b.qw = qw;
    b.powerKick = powerKick;
    b.possessedBy = possessedBy;
  }

  /**
   * THE BALL IS IN THIS SLOT'S HANDS. (§4.5, Law 15)
   *
   * A companion to `setBall`, not a replacement for it — `setBall` still
   * carries the only ball POSITION and it is still the sim's. This says whose
   * hands to draw it in; see `RenderState.ballHeldBy`. Callers derive the
   * argument from `game/ballHold.ts` so every loop agrees.
   *
   * Not defaulted-on anywhere: `beginFrame` resets it to loose every frame, so
   * a caller that stops feeding it (or never starts) simply gets the ball drawn
   * at the simulated position, which is the behaviour that predates this.
   */
  setBallHold(slot: number, pose: BallHoldPose): void {
    const s = this.state;
    if (slot < 0 || slot >= MAX_PLAYERS || pose === BallHoldPose.None) {
      s.ballHeldBy = -1;
      s.ballHoldPose = BallHoldPose.None;
      return;
    }
    s.ballHeldBy = slot;
    s.ballHoldPose = pose;
  }

  /** Feed the in-world LED scoreboards. Strings are only ASSIGNED (no work) --
   *  the stadium repaints its texture only on a value change, so calling this
   *  every frame is free. */
  setScoreboard(homeName: string, awayName: string, scoreA: number, scoreB: number,
    clockS: number, phase: string): void {
    const sb = this.state.scoreboard;
    sb.show = true;
    sb.homeName = homeName;
    sb.awayName = awayName;
    sb.scoreA = scoreA;
    sb.scoreB = scoreB;
    sb.clockS = clockS;
    sb.phase = phase;
  }

  setControlled(x: number, y: number, z: number, blend: number, pulseS: number, yaw: number): void {
    const s = this.state;
    s.controlledShow = true;
    s.controlledX = x;
    s.controlledY = y;
    s.controlledZ = z;
    s.controlledBlend = blend;
    s.controlledPulse = pulseS;
    s.controlledYaw = yaw;
  }

  hideControlled(): void {
    this.state.controlledShow = false;
  }

  /**
   * The stamina-boost aura (render/boostAura.ts). Position-fed per frame like
   * the controlled ring; `tier` 0 hides it, so a loop that stops calling this —
   * or calls it with 0 — costs nothing. `beginFrame` defaults the tier to 0, so
   * a loop that never calls it at all draws no aura rather than a stale one.
   */
  setBoostAura(slot: number, x: number, y: number, z: number, tier: number): void {
    const s = this.state;
    s.boostAuraTier = tier > 0 ? Math.min(2, Math.round(tier)) : 0;
    s.boostAuraSlot = slot >= 0 && slot < MAX_PLAYERS ? slot : -1;
    s.boostAuraX = x;
    s.boostAuraY = y;
    s.boostAuraZ = z;
  }

  /** Pass-target indicator (§4.2, 3C): faint circle under the teammate the
   *  charging pass resolves to. Position-fed per frame, like the ring. */
  /** `id` is the RECEIVER's slot. The indicator snaps rather than eases when it
   *  changes, so the ring never glides across grass between two players. */
  setPassTarget(x: number, y: number, z: number, id = -1): void {
    const s = this.state;
    s.passTargetShow = true;
    s.passTargetX = x;
    s.passTargetY = y;
    s.passTargetZ = z;
    s.passTargetId = id;
  }

  /**
   * CORNER LANDING TRAIL (render/cornerTrail.ts). Fed straight from
   * `previewCornerDelivery`'s output; `points` is BORROWED for this frame only —
   * the subsystem copies out of it during `update` and never retains it.
   */
  setCornerTrail(
    points: Float32Array, count: number,
    landX: number, landZ: number, landed: boolean, exact: boolean,
  ): void {
    const s = this.state;
    // ⚠ THE ARC IS AN AIMING AID FOR THE TAKER, NOT A MAP FOR THE DEFENCE
    // (owner, 2026-08-16: "for the corner kick dont show the trajectory inside
    // the pitch so the bots can kick near the box and their teammates hit with
    // head and do goal"). Drawing the whole flight into the box tells everyone
    // watching exactly where the ball will land, which is both a spoiler and
    // the reason a corner never feels like a contest. The LANDING RING stays -
    // that is the target the taker is aiming at - and the polyline is gone.
    s.cornerTrailShow = false;
    s.cornerTrailPoints = points;
    s.cornerTrailCount = count;
    s.cornerTrailLandX = landX;
    s.cornerTrailLandZ = landZ;
    s.cornerTrailLanded = landed;
    s.cornerTrailExact = exact;
  }

  /**
   * Fire the goal celebration — confetti over the bowl, and fireworks if it is
   * a night match. Kit colours are the SCORING side's, so the paper matches
   * whoever just scored.
   */
  celebrate(
    x: number,
    z: number,
    kitPrimary: readonly [number, number, number],
    kitSecondary: readonly [number, number, number],
    night: boolean,
  ): void {
    this.celebrationHandle?.burst(x, z, kitPrimary, kitSecondary, night);
  }

  /** Kill any celebration in flight (quit to menu, new match). */
  clearCelebration(): void {
    this.celebrationHandle?.clear();
  }

  /**
   * Drive the crowd's reaction (§6.6) — 0 ambient, 1 a goal-roar. Smoothed
   * inside the stadium subsystem, safe to call every frame or never. Reaches
   * whichever bowl is mounted — procedural or anime — via CrowdControl.
   */
  setCrowdExcitement(v: number): void {
    this.stadiumHandle?.setExcitement(v);
  }

  /**
   * Localized crowd surge at a world point (§6.6) — near-misses, corners, the
   * ball bearing down on a goal. Best-effort: only bowls that sector their
   * crowd implement it (see CrowdControl.exciteAt), elsewhere it's a no-op.
   */
  exciteCrowdAt(x: number, z: number, strength: number): void {
    this.stadiumHandle?.exciteAt?.(x, z, strength);
  }

  setBallArrow(show: boolean, x: number, y: number, z: number, yaw: number): void {
    const s = this.state;
    s.ballArrowShow = show;
    s.ballArrowX = x;
    s.ballArrowY = y;
    s.ballArrowZ = z;
    s.ballArrowYaw = yaw;
  }

  /**
   * Kick-aim arrow (§2/§5, LIVE FOOTBALL POLISH): ground arrow from the
   * controlled player toward its live aimYaw, visible only while that player
   * has controllable possession (the caller gates the call on `ctx.possessing`
   * — see game/practice.ts + main.ts). `charge` is the active shoot/pass charge
   * fraction (0..1, 0 when nothing is charging) and scales length/opacity.
   * `curl` is InputState's live curl accumulator ([-1, 1], 0 outside a charge)
   * — bends the indicator's curve-shaft so the curve mechanic is visible while
   * charging, not just felt in the eventual ball flight.
   */
  setAimArrow(show: boolean, x: number, y: number, z: number, yaw: number, charge: number, curl = 0): void {
    const s = this.state;
    s.aimArrowShow = show;
    s.aimArrowX = x;
    s.aimArrowY = y;
    s.aimArrowZ = z;
    s.aimArrowYaw = yaw;
    s.aimArrowCharge = charge;
    s.aimArrowCurl = curl;
  }

  /**
   * REFEREE SIGNAL (§5.6) — "the referee just whistled / just showed a card".
   *
   * EDGE-triggered, unlike every other setter on this shell: call it once, on
   * the tick the sim raised the event, and the channel holds the cue for its own
   * duration and then clears (see refSignal.ts). Callers do not poll it and
   * must not re-push the same event every frame — that would restart the hold
   * forever and the cue would never go away.
   *
   * Feed it from a loop that owns `StepResult.events` (practice / solo / arena)
   * via `feedRefSignals`, or — online, where rules live on the server — from the
   * decoded RefereeSignal wire frame (shared/net/ruleevent.ts). `x`/`z` is the
   * SPOT being signalled, which is what makes the referee jog there instead of
   * carrying on with his diagonal.
   */
  signalReferee(kind: RefSignalKind, tick: number, x: number, z: number, phase: MatchPhase): void {
    if (kind === RefSignalKind.None) return;
    refRaiseScratch.kind = kind;
    refRaiseScratch.tick = tick;
    refRaiseScratch.x = x;
    refRaiseScratch.z = z;
    refRaiseScratch.phase = phase;
    raiseRefSignal(this.state.refSignal, refRaiseScratch);
  }

  applyCamera(pose: CameraPose): void {
    this.camera.position.set(pose.x, pose.y, pose.z);
    this.lookAt.set(pose.lookX, pose.lookY, pose.lookZ);
    this.camera.lookAt(this.lookAt);
    if (this.camera.fov !== pose.fov) {
      this.camera.fov = pose.fov;
      this.camera.updateProjectionMatrix();
    }
  }

  /**
   * True once every cast character model has finished loading (or the cast
   * subsystem is disabled/off, which reads as trivially ready — see
   * castPlayerVisual.ts's own `ready=true` fallback for the same cases).
   * `false` for the whole ~10-15s the cast streams in tier by tier, during
   * which castPlayerVisual's own `update()` draws nothing at all (no players,
   * no officials, no subs — an empty pitch). A transition screen that
   * dismisses on world/connection readiness alone, without also checking
   * this, hands the player exactly that empty pitch, or players popping in
   * mid-load a moment later.
   */
  get castReady(): boolean {
    return this.castHandle?.ready ?? true;
  }

  /**
   * The player animation system, or null when the capsule fallback is drawn.
   *
   * Exposed so the presentation layer can hang off the ANIMATION's ball-contact
   * beat (`contacts()`, raised once per action and timed off the simulation's
   * own ACTION_DEFS table) and so the dev overlay can name the semantic command
   * a player is actually playing. Deliberately read-mostly: gameplay drives
   * animation by feeding `setPlayer(..., anim)`, never by reaching in here.
   */
  get playerAnimation(): PlayerVisualHandle | null {
    return this.playersHandle;
  }

  render(): void {
    for (const s of this.subsystems) s.update(this.state, this.ctx);
    if (this.compositor) {
      this.compositor.composite(this.ctx);
    } else {
      this.gl.render(this.scene, this.camera);
    }
  }

  resize(): void {
    const w = this.container.clientWidth || window.innerWidth;
    const h = this.container.clientHeight || window.innerHeight;
    this.gl.setSize(w, h, false);
    this.ctx.viewportWidth = Math.floor(w * this.gl.getPixelRatio());
    this.ctx.viewportHeight = Math.floor(h * this.gl.getPixelRatio());
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    for (const s of this.subsystems) s.onResize?.(this.ctx);
  }

  dispose(): void {
    for (const s of this.subsystems) s.dispose?.();
    this.gl.dispose();
    // gl.dispose() releases three's own GPU objects but leaves the WebGL context
    // itself alive, and a browser only allows a handful before it starts
    // discarding the oldest. Solo practice builds a whole render stack per
    // visit, so without this an evening of entering and leaving training
    // silently burns through the budget.
    this.gl.forceContextLoss();
    this.gl.domElement.remove();
    // The dev handle pins this renderer, its scene and its context for as long
    // as the page lives. It is overwritten on the next mount, so it never held
    // more than one dead generation — but one dead generation is still a whole
    // scene graph and an un-lost context, and it made every heap snapshot taken
    // during this investigation harder to read.
    if (import.meta.env.DEV) {
      const w = window as unknown as Record<string, unknown>;
      if (w['__fg'] === this) delete w['__fg'];
    }
  }
}
